import React, { createContext, useState, useContext, useEffect } from 'react';
import { onSnapshot, collection, query, where } from 'firebase/firestore';
import { firestore } from '../firebase';
import { useAuth } from './AuthContext';
import { isTrialActive, toTrialDate, trialDaysRemaining } from '../Utils/subscriptionFeatures';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

function toDate(value) {
  return toTrialDate(value);
}

function resolveSubscriptionState(docs) {
  if (!docs.length) {
    return {
      subscriptionStatus: 'free',
      cancelAtPeriodEnd: false,
      currentPeriodEnd: null,
      trialEndsAt: null,
    };
  }

  let subscriptionStatus = 'free';
  let cancelAtPeriodEnd = false;
  let currentPeriodEnd = null;
  let trialEndsAt = null;

  for (const docSnap of docs) {
    const data = docSnap.data() || {};
    const status = data.subscriptionStatus || 'free';
    const periodEnd = toDate(data.currentPeriodEnd);
    const trialEnd = toDate(data.trialEndsAt);

    if (trialEnd && (!trialEndsAt || trialEnd > trialEndsAt)) {
      trialEndsAt = trialEnd;
    }

    if (status === 'premium') {
      subscriptionStatus = 'premium';
      cancelAtPeriodEnd = cancelAtPeriodEnd || !!data.cancelAtPeriodEnd;
      if (periodEnd && (!currentPeriodEnd || periodEnd > currentPeriodEnd)) {
        currentPeriodEnd = periodEnd;
      }
    } else if (status === 'canceling' && periodEnd && periodEnd.getTime() > Date.now()) {
      subscriptionStatus = 'canceling';
      cancelAtPeriodEnd = true;
      if (!currentPeriodEnd || periodEnd > currentPeriodEnd) {
        currentPeriodEnd = periodEnd;
      }
    }
  }

  if (
    subscriptionStatus === 'premium' &&
    cancelAtPeriodEnd &&
    currentPeriodEnd &&
    currentPeriodEnd.getTime() > Date.now()
  ) {
    subscriptionStatus = 'canceling';
  }

  return { subscriptionStatus, cancelAtPeriodEnd, currentPeriodEnd, trialEndsAt };
}

export const SubscriptionProvider = ({ children }) => {
  const { userId } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState('free');
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(null);
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!userId) {
      setSubscriptionStatus('free');
      setCancelAtPeriodEnd(false);
      setCurrentPeriodEnd(null);
      setTrialEndsAt(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const usersRef = collection(firestore, 'users');
    const q = query(usersRef, where('uid', '==', userId));

    const unsubscribeSnapshot = onSnapshot(
      q,
      (snapshot) => {
        const state = resolveSubscriptionState(snapshot.docs);
        setSubscriptionStatus(state.subscriptionStatus);
        setCancelAtPeriodEnd(state.cancelAtPeriodEnd);
        setCurrentPeriodEnd(state.currentPeriodEnd);
        setTrialEndsAt(state.trialEndsAt);
        setLoading(false);
      },
      (error) => {
        console.error('Subscription listener error:', error);
        setLoading(false);
      }
    );

    return () => unsubscribeSnapshot();
  }, [userId]);

  // Re-evaluate trial expiry while the app stays open past midnight / trial end.
  useEffect(() => {
    if (!trialEndsAt || !isTrialActive(trialEndsAt)) return undefined;
    const ms = Math.min(
      Math.max(trialEndsAt.getTime() - Date.now() + 1000, 1000),
      60 * 60 * 1000
    );
    const id = setTimeout(() => setTick((n) => n + 1), ms);
    return () => clearTimeout(id);
  }, [trialEndsAt]);

  const trialing = isTrialActive(trialEndsAt);
  const paid =
    subscriptionStatus === 'premium' || subscriptionStatus === 'canceling';
  const isPremium = paid || trialing;
  const daysLeftInTrial = trialing ? trialDaysRemaining(trialEndsAt) : 0;

  const value = {
    subscriptionStatus: paid ? subscriptionStatus : trialing ? 'trial' : 'free',
    cancelAtPeriodEnd,
    currentPeriodEnd,
    trialEndsAt,
    isTrialing: trialing && !paid,
    daysLeftInTrial,
    loading,
    isPremium,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider;
